import {
  Stack,
  Avatar,
  useMediaQuery,
  Typography
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { FileText, Link2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { TopicCardProps } from "./TopicCardPost.types";
import {
  CtaCommunityOverlay,
  ModifyContentModal,
  CommentsSection
  ,Link } from "..";
import { ButtonDigited } from "../../components/Button/Button.variants";
import { CommonWidgetContainer } from "../../components/Commons/CommonWidgetContainer.styles";
import { DeleteContentModal } from "../../components/DeleteContentModal";
import {
  BodyTypography,
  OwnerNameTypography,
  TopicCardBody,
  TopicCardTop
} from "../../components/TopicCard/TopicCard.styles";
import { TopicCardSignature } from "../../components/TopicCardSignature";
import { POST_TYPE, ROLES } from "../../consts";
import { useAuthStore } from "../../stores";
import { TypographyTextTag } from "../../styles/Typography/TypographyTextTag.styles";
import { stringAvatar, useGetCreatedLabel } from "../../utils/community";
import { BadgeBox } from "../Commons/BadgeBox";

export function TopicCardPost({
  attachments,
  body,
  changed,
  commentsNumber,
  created,
  id,
  likes,
  ownerImage,
  ownerName,
  topicOwner,
  selectedFilter,
  selectedTopic
}: TopicCardProps) {
  const theme = useTheme();
  const { t } = useTranslation();

  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isModifyOpen, setIsModifyOpen] = useState(false);
  const[showComments,setShowComments]=useState(false);
  const userData = useAuthStore(state => state.userData);

  const isModerator = userData?.roles.some(role => role === ROLES.BUSINESS_ADMIN || role === ROLES.PRODUCT_OWNER) ;
  const userId = useAuthStore(state => state.userData?.id_user);
  const isEditable =  (userId && topicOwner == userId.toString());

  const isModified = created !== changed;

  return (
    <CommonWidgetContainer
      aria-label={ t("community_post_aria_label",{ subject:ownerName,post_title:body,time:useGetCreatedLabel(created)||"" }) }
      tabIndex={ 0 }
      sx={ {
        width:"100% !important",
        maxWidth:"100%" ,
        borderRadius: "1rem",
        padding: "1.5rem",
        "& p": {
          color: theme.customColors.exploreCardText
        },
        "& span": {
          color: theme.customColors.exploreCardText 
        },
        "& .icon path": {
          fill: `${theme.customColors.exploreCardText} !important`
        },
        "&:focus-visible": {
          outline: `2px solid ${theme.customColors.headerIcon}`,
          outlineOffset: "-3px"
        }
      } }
      hasSidePadding={ true }
      hoverBorderAccent={ false }
    >
      <TopicCardTop
        tabIndex={ -1 }
        direction={ "row" }
        gap={ "16px" }
        alignItems={ "center" }
      >
        <Avatar
          aria-hidden={ true }
          alt={ `${t("avatar_user")} ${ownerName}` }
          { ...stringAvatar(ownerName) }
          src={ ownerImage }
          sx={ {
            height: isMobile ? "32px" : "56px",
            width: isMobile ? "32px" : "56px",
            alignSelf: "start"
          } }
        />
        <Stack
          gap={ 1 }
          flexGrow={ 1 }
        >
          <Stack
            aria-hidden={ true }
            direction={ "row" }
            justifyContent={ "space-between" }
            alignItems={ "start" }
            gap={ "1rem" }
          >
            <Stack
              gap={ 0.5 }
            >
              <OwnerNameTypography variant="h4">
                { ownerName }
              </OwnerNameTypography>
              <TopicCardSignature
                created={  useGetCreatedLabel(created)  }
                isModified={ isModified }
                size={ "medium" }
                color={ theme.customColors.exploreCardText }
              />
            </Stack>
            <Stack
              alignItems={ "end" }
            >
              <BadgeBox
                background={ theme.customColors.backgroundSecondary }
                textColor={ "inherit" }
                border={ `1px solid ${theme.customColors.textTag}` }>
                <Stack
                  direction={ "row" }
                  alignItems={ "center" }
                  gap={ "0.44rem" }
                >
                  <FileText size={ 16 } color={ theme.customColors.textTag }/>
                  <TypographyTextTag>
                    { t("post") }
                  </TypographyTextTag>
                </Stack>
              </BadgeBox>
              {
                isEditable || isModerator ?
                  <Stack
                    direction="row"
                    alignItems={ "flex-end" }
                    justifyContent={ "end" }
                    gap={ "0.5rem" }
                  >
                    {
                      isEditable ?
                        <ButtonDigited
                          aria-label={ `${t("modify")} post` }
                          typology="tertiary"
                          sx={ {
                            fontSize: "0.75rem"
                          } }
                          
                          onClick={ () => setIsModifyOpen(true) }
                        >
                          { t("modify") }
                        </ButtonDigited>  : null
                    }

                    <ButtonDigited
                      typology="tertiary"
                      aria-label={ `${t("delete")} post` }
                      sx={ {
                        paddingRight:"0",
                        fontSize: "0.75rem"
                      } }
                      onClick={ () => setIsDeleteOpen(true) }
                    >
                      { t("delete") }
                    </ButtonDigited>
                  </Stack>  :
                  null
              }
            </Stack>
          </Stack>
        </Stack>
      </TopicCardTop>

      <TopicCardBody>
        <BodyTypography>
          { body }

        </BodyTypography>
        {
          attachments?.["link"]?.[0]?.content  ?
            <Link
              href={ attachments?.["link"]?.[0]?.content }
              target={ "_blank" }
              rel="noreferrer"
              sx={ {
                color: theme.customColors.exploreCardText,
                alignItems: "start",
                border: `1px solid ${theme.customColors.border}`,
                borderRadius: "0.5rem",
                display: "flex",
                flexDirection: "row",
                gap: "0.5rem",
                height: "2rem",
                maxWidth: "20rem",
                marginTop: "1rem",
                padding: "0.5rem",
                width: "fit-content" 

              } }
            >
              <Link2 size={ "1rem" }/>
              <Typography
                fontSize={ "0.75rem" }
                lineHeight={ "1rem" }
              >
                {
                  attachments?.["link"]?.[0]?.name ||
                    getUrlname(attachments?.["link"]?.[0]?.content )
                }
              </Typography>
            </Link>

            :
            null
        }

      </TopicCardBody>

      <CtaCommunityOverlay
        id={ id }
        comments={ commentsNumber }
        likes={ likes }
        commentsOpened={ showComments }
        commentsClickAction={ () => {
          setShowComments(!showComments);
        } }
        selectedFilter={ selectedFilter }
        selectedTopic={ selectedTopic }
      />

      { showComments &&
        <CommentsSection
          topicOwnerId={ topicOwner }
          selectedFilter={ selectedFilter }
          selectedTopic={ selectedTopic }
          discussionId={ id }
          enabled={ showComments }
          className={ showComments ? "visible" : "" }
        />
      }

      {
        isDeleteOpen ?

          <DeleteContentModal
            selectedFilter={ selectedFilter }
            selectedTopic={ selectedTopic }
            id={ id }
            isModerator={ Boolean(isModerator) }
            postType={ POST_TYPE.FREE }
            onClose={ ()=> {
              setIsDeleteOpen(false);
            } }
            closeDrawer={ ()=> {
              setIsDeleteOpen(false);
            } }
            open={ isDeleteOpen }
          /> : null
      }

      {
        isModifyOpen ?
          <ModifyContentModal
            selectedFilter={ selectedFilter }
            selectedTopic={ selectedTopic }
            open={ isModifyOpen }
            onClose={ () => setIsModifyOpen(false) }
            id={ id }
            postType={ POST_TYPE.FREE }
            linklabel={ attachments?.["link"]?.[0]?.name ?? "" }
            linkvalue={ attachments?.["link"]?.[0]?.content ?? "" }
            body={ body }
            closeDrawer={ () => setIsModifyOpen(false) }
          /> : null

      }



    </CommonWidgetContainer>
  );
  function getUrlname(url: string) {

    return url.replace(/^https?:\/\//, "");
  }


}
